import { CarType, CourierJson } from 'types/bundleTypes';
import { IShift } from './shiftApi';
import dayjs, { Dayjs } from 'dayjs';
import Swal from 'sweetalert2';

export const SHIFT_PLANNER_CHANNEL = 'shift-planner';

export enum JobType {
  ALL,
  COURIER,
  SORTER,
}

export enum ShiftOverviewStatus {
  ALL = 'All',
  BOOKED = 'Booked',
  REQUESTED = 'Requested',
  DECLINED = 'Declined',
  DISPATCH_DECLINED = 'Dispatch declined',
  CANCELED = 'Canceled',
}

export enum CourierVehicleType {
  ALL = 'ALL',
  CARGO_BIKE = 'CARGO_BIKE',
  NORMAL_BIKE = 'NORMAL_BIKE',
  HYBRID = 'HYBRID',
  ELECTRIC = 'ELECTRIC',
  DIESEL = 'DIESEL',
  PETROL = 'PETROL',
}

export enum ShiftStatusType {
  DELETED = 'DELETED',
  BOOKED = 'BOOKED',
  OPEN = 'OPEN',
  WISH = 'WISH',
  REQUESTED = 'REQUESTED',
  DECLINED = 'DECLINED',
  DISPATCH_DECLINED = 'DISPATCH_DECLINED',
  CANCELED = 'CANCELED',
}

export enum ExtraWorkType {
  SORTING = 'SORTING',
  INBOUND = 'INBOUND',
  BACKUP = 'BACKUP',
}

export const initialExtraWorkList: ExtraWorkWithCustomName[] = [
  {
    name: 'Backup (200)',
    extraWork: { typeOfExtraWork: ExtraWorkType.BACKUP, payment: 200 },
  },
  {
    name: 'Inbound (100)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 100 },
  },
  {
    name: 'Inbound (200)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 200 },
  },
  {
    name: 'Inbound (400)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 400 },
  },
  {
    name: 'Inbound (500)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 500 },
  },
  {
    name: 'Inbound (600)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 600 },
  },
  {
    name: 'Inbound (700)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 700 },
  },
  {
    name: 'Inbound (800)',
    extraWork: { typeOfExtraWork: ExtraWorkType.INBOUND, payment: 800 },
  },
  {
    name: 'Sorting (187)',
    extraWork: { typeOfExtraWork: ExtraWorkType.SORTING, payment: 187 },
  },
  {
    name: 'Sorting (282)',
    extraWork: { typeOfExtraWork: ExtraWorkType.SORTING, payment: 282 },
  },
  {
    name: 'Sorting (375)',
    extraWork: { typeOfExtraWork: ExtraWorkType.SORTING, payment: 375 },
  },
  {
    name: 'Sorting (562)',
    extraWork: { typeOfExtraWork: ExtraWorkType.SORTING, payment: 562 },
  },
  {
    name: 'Sorting (750)',
    extraWork: { typeOfExtraWork: ExtraWorkType.SORTING, payment: 750 },
  },
];

export const SHIFT_COLOUR_ORDER = [
  'yellow',
  'green',
  'red',
  'grey',
  'purple',
  'blue',
];

export const mapShiftToColourGroup = (shiftStatus: ShiftStatusType): string => {
  switch (shiftStatus) {
    case ShiftStatusType.BOOKED:
      return 'green';
    case ShiftStatusType.REQUESTED:
      return 'yellow';
    case ShiftStatusType.OPEN:
      return 'blue';
    case ShiftStatusType.CANCELED:
      return 'grey';
    case ShiftStatusType.DISPATCH_DECLINED:
      return 'purple';
    default:
      return 'red';
  }
};

export const mapCourierIdsToShiftStatus = (
  courierShifts: IShift[]
): {
  booked: number[];
  requested: number[];
  declined: number[];
  canceled: number[];
  dispatchDeclined: number[];
} => {
  const declined = courierShifts
    .filter(shift => shift.status === ShiftStatusType.DECLINED)
    .map(courier => courier.courierId);
  const dispatchDeclined = courierShifts
    .filter(shift => shift.status === ShiftStatusType.DISPATCH_DECLINED)
    .map(courier => courier.courierId);
  const canceled = courierShifts
    .filter(shift => shift.status === ShiftStatusType.CANCELED)
    .map(courier => courier.courierId);
  const requested = courierShifts
    .filter(shift => shift.status === ShiftStatusType.REQUESTED)
    .map(courier => courier.courierId);
  const booked = courierShifts
    .filter(shift => shift.status === ShiftStatusType.BOOKED)
    .map(courier => courier.courierId);

  return {
    booked,
    requested,
    declined,
    canceled,
    dispatchDeclined,
  };
};

export interface ExtraWorkWithCustomName {
  name: string;
  extraWork: { typeOfExtraWork: string; payment: number };
}

export interface ShiftsById {
  [id: string]: IShift;
}

export interface ShiftsByDate {
  [date: string]: IShift[];
}

export interface ShiftsByCourier {
  [courierId: string]: IShift[];
}

export interface CourierCollection {
  list: CourierJson[];
  byId: {
    [id: number]: CourierJson;
  };
}

export interface ShiftCountByStatus {
  booked: number;
  requested: number;
  declined: number;
  dispatchDeclined: number;
  canceled: number;
}

export interface ShiftCount {
  previousWeek: { [courierId: number]: ShiftCountByStatus };
  currentWeek: { [courierId: number]: ShiftCountByStatus };
}

export interface ShiftCollection {
  byDate: ShiftsByDate;
  currentShifts: IShift[];
  shiftCount: ShiftCount;
}

export const countBookedSorters = (shifts: IShift[]): number =>
  shifts.filter(
    shift =>
      shift.status === ShiftStatusType.BOOKED &&
      shift.extraWork.some(e => e.typeOfExtraWork === 'SORT')
  ).length;

export const getBookedCourierIds = (shifts: IShift[]): number[] =>
  shifts
    .filter(shift => shift.status === ShiftStatusType.BOOKED)
    .map(shift => shift.courierId);

export const countElectricCouriers = (
  courierIds: number[],
  couriers: CourierCollection
): number =>
  courierIds.filter(id => couriers.byId[id]?.carType === CarType.ELECTRIC)
    .length;

export const countFossilCouriers = (
  courierIds: number[],
  couriers: CourierCollection
): number =>
  courierIds.filter(id =>
    [CarType.PETROL, CarType.DIESEL].includes(couriers.byId[id]?.carType)
  ).length;

export const countHybridCouriers = (
  courierIds: number[],
  couriers: CourierCollection
): number =>
  courierIds.filter(id => couriers.byId[id]?.carType === CarType.HYBRID).length;

export const countBikeCouriers = (
  courierIds: number[],
  couriers: CourierCollection
): number =>
  courierIds.filter(id => couriers.byId[id]?.carType === CarType.NORMAL_BIKE)
    .length;

export const countCargoBikeCouriers = (
  courierIds: number[],
  couriers: CourierCollection
): number =>
  courierIds.filter(id => couriers.byId[id]?.carType === CarType.CARGO_BIKE)
    .length;

export type Comparator<T> = (a: T, b: T) => number;

export const stringComparator = (a: string, b: string): number =>
  a.localeCompare(b);

export const numberComparator = (a: number, b: number): number =>
  a < b ? -1 : 1;

export const dateComparator = (a: string, b: string): number =>
  new Date(a).getTime() - new Date(b).getTime();

export const afterPeriodChangesPossible = (
  columnDate: Dayjs,
  daysUntilCalculation: number
): boolean => {
  const columnDateRef = dayjs(columnDate).startOf('day');
  const cutoffDay = columnDateRef.isBefore(
    columnDateRef.date(15).startOf('day')
  )
    ? columnDateRef.date(15 + daysUntilCalculation).startOf('day')
    : columnDateRef
        .date(1 + daysUntilCalculation)
        .add(1, 'month')
        .startOf('day');
  return dayjs().startOf('day').isAfter(cutoffDay);
};

export const handleError = error => {
  Swal.fire({
    position: 'center',
    icon: 'error',
    title: error,
    showConfirmButton: true,
  });
};
